import React, { useState, useEffect, useMemo } from 'react';
import {
  Award,
  Settings,
  ChevronLeft,
  Plus,
  Trash2,
  Edit2,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  Save,
  CheckCircle2,
  AlertTriangle,
  Sliders,
  ShieldCheck,
  FileText,
  Search,
  Copy,
  Check,
  X,
  Trophy,
  Lock
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { isManagementOrTrainer } from '../../utils/formatters';
import { GradingHeaderHero } from '../../components/grading/GradingHeaderHero';
import { navigateBack } from '../../utils/scrollRestoration';
import {
  GradingCriteriaItem,
  BengkungGradingScheme,
  GradingGeneralRules,
  getCustomGradingSchemes,
  saveCustomGradingSchemes,
  resetCustomGradingSchemes,
  getGradingGeneralRules,
  saveGradingGeneralRules,
  UJIAN_GRADING_SYLLABUS_SCHEMES
} from '../../data/ujianGradingSyllabus';

const CRITERIA_CATEGORIES: GradingCriteriaItem['category'][] = [
  'Hormat & Adab',
  'Gerak Asas',
  'Jurus Asas',
  'Buah Pukulan',
  'Teknik Beladiri',
  'Asas Tari',
  'Senjata',
  'Kuncian',
  'Dewan Pendekar'
];

export const UjianGradingSettingsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const isManagement = Boolean(currentUser && isManagementOrTrainer(currentUser));

  // Active scheme key
  const [selectedSchemeKey, setSelectedSchemeKey] = useState<string>('putih1');
  const [schemes, setSchemes] = useState<Record<string, BengkungGradingScheme>>(() => getCustomGradingSchemes());
  const [generalRules, setGeneralRules] = useState<GradingGeneralRules>(() => getGradingGeneralRules());

  // Search & Filter within criteria list
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Editing state for an existing criterion
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    title: string;
    category: GradingCriteriaItem['category'];
    description: string;
    isUlangkaji: boolean;
    maxScore: number;
  }>({
    title: '',
    category: 'Gerak Asas',
    description: '',
    isUlangkaji: false,
    maxScore: 5
  });

  // Adding new criterion state
  const [isAddingNew, setIsAddingNew] = useState<boolean>(false);
  const [newForm, setNewForm] = useState<{
    title: string;
    category: GradingCriteriaItem['category'];
    description: string;
    isUlangkaji: boolean;
    maxScore: number;
  }>({
    title: '',
    category: 'Gerak Asas',
    description: '',
    isUlangkaji: false,
    maxScore: 5
  });

  // Has unsaved changes notice
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [copiedJson, setCopiedJson] = useState<boolean>(false);

  // Load latest on mount or when external update happens
  useEffect(() => {
    const handleUpdate = () => {
      setSchemes(getCustomGradingSchemes());
      setGeneralRules(getGradingGeneralRules());
    };
    window.addEventListener('asmm_grading_syllabus_updated', handleUpdate);
    window.addEventListener('asmm_grading_rules_updated', handleUpdate);
    return () => {
      window.removeEventListener('asmm_grading_syllabus_updated', handleUpdate);
      window.removeEventListener('asmm_grading_rules_updated', handleUpdate);
    };
  }, []);

  const activeScheme = useMemo(() => {
    return schemes[selectedSchemeKey] || schemes['putih1'] || UJIAN_GRADING_SYLLABUS_SCHEMES['putih1'];
  }, [schemes, selectedSchemeKey]);

  // Filtered criteria list for active scheme
  const filteredCriteria = useMemo(() => {
    if (!activeScheme || !activeScheme.criteria) return [];
    return activeScheme.criteria.filter((item) => {
      if (categoryFilter !== 'all' && item.category !== categoryFilter) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesTitle = item.title.toLowerCase().includes(q);
        const matchesDesc = (item.description || '').toLowerCase().includes(q);
        const matchesCat = item.category.toLowerCase().includes(q);
        return matchesTitle || matchesDesc || matchesCat;
      }
      return true;
    });
  }, [activeScheme, categoryFilter, searchQuery]);

  // Update passing score percentage
  const handlePassingScoreChange = (newPct: number) => {
    const clamped = Math.max(10, Math.min(100, Math.round(newPct)));
    setSchemes((prev) => ({
      ...prev,
      [selectedSchemeKey]: {
        ...prev[selectedSchemeKey],
        passingScorePercentage: clamped
      }
    }));
    setHasUnsavedChanges(true);
  };

  // Update display name
  const handleDisplayNameChange = (newName: string) => {
    setSchemes((prev) => ({
      ...prev,
      [selectedSchemeKey]: {
        ...prev[selectedSchemeKey],
        displayName: newName
      }
    }));
    setHasUnsavedChanges(true);
  };

  // Move criterion UP
  const handleMoveUp = (criterionId: string) => {
    setSchemes((prev) => {
      const currentList = [...prev[selectedSchemeKey].criteria];
      const index = currentList.findIndex((c) => c.id === criterionId);
      if (index <= 0) return prev;

      const temp = currentList[index - 1];
      currentList[index - 1] = currentList[index];
      currentList[index] = temp;

      // Re-index orderNumber cleanly
      const reindexed = currentList.map((c, i) => ({
        ...c,
        orderNumber: i + 1
      }));

      return {
        ...prev,
        [selectedSchemeKey]: {
          ...prev[selectedSchemeKey],
          criteria: reindexed
        }
      };
    });
    setHasUnsavedChanges(true);
  };

  // Move criterion DOWN
  const handleMoveDown = (criterionId: string) => {
    setSchemes((prev) => {
      const currentList = [...prev[selectedSchemeKey].criteria];
      const index = currentList.findIndex((c) => c.id === criterionId);
      if (index === -1 || index >= currentList.length - 1) return prev;

      const temp = currentList[index + 1];
      currentList[index + 1] = currentList[index];
      currentList[index] = temp;

      // Re-index orderNumber cleanly
      const reindexed = currentList.map((c, i) => ({
        ...c,
        orderNumber: i + 1
      }));

      return {
        ...prev,
        [selectedSchemeKey]: {
          ...prev[selectedSchemeKey],
          criteria: reindexed
        }
      };
    });
    setHasUnsavedChanges(true);
  };

  // Delete criterion
  const handleDeleteCriterion = (criterionId: string, title: string) => {
    if (!window.confirm(`Adakah anda pasti untuk memadam sukatan "${title}" daripada bengkung ini?`)) {
      return;
    }

    setSchemes((prev) => {
      const filtered = prev[selectedSchemeKey].criteria.filter((c) => c.id !== criterionId);
      const reindexed = filtered.map((c, i) => ({
        ...c,
        orderNumber: i + 1
      }));

      return {
        ...prev,
        [selectedSchemeKey]: {
          ...prev[selectedSchemeKey],
          criteria: reindexed
        }
      };
    });
    setHasUnsavedChanges(true);
    showToast('info', 'Kriteria Dipadam', `Sukatan "${title}" telah dikeluarkan.`);
  };

  // Start editing a criterion
  const handleStartEdit = (item: GradingCriteriaItem) => {
    setEditingItemId(item.id);
    setEditForm({
      title: item.title,
      category: item.category,
      description: item.description || '',
      isUlangkaji: Boolean(item.isUlangkaji),
      maxScore: item.maxScore || 5
    });
  };

  // Save edited criterion
  const handleSaveEdit = (criterionId: string) => {
    if (!editForm.title.trim()) {
      showToast('error', 'Ralat', 'Tajuk kriteria sukatan tidak boleh dibiarkan kosong.');
      return;
    }

    setSchemes((prev) => {
      const updatedList = prev[selectedSchemeKey].criteria.map((c) => {
        if (c.id === criterionId) {
          return {
            ...c,
            title: editForm.title.trim(),
            category: editForm.category,
            description: editForm.description.trim(),
            isUlangkaji: editForm.isUlangkaji,
            maxScore: editForm.maxScore || 5
          };
        }
        return c;
      });

      return {
        ...prev,
        [selectedSchemeKey]: {
          ...prev[selectedSchemeKey],
          criteria: updatedList
        }
      };
    });

    setEditingItemId(null);
    setHasUnsavedChanges(true);
    showToast('success', 'Kemaskini Sukatan', `Kriteria "${editForm.title.trim()}" telah dikemaskini.`);
  };

  // Add new criterion
  const handleAddNewCriterion = () => {
    if (!newForm.title.trim()) {
      showToast('error', 'Ralat', 'Sila masukkan tajuk pergerakan atau kriteria sukatan.');
      return;
    }

    const newId = `${selectedSchemeKey}-${Date.now().toString().slice(-6)}`;
    const currentList = activeScheme.criteria || [];
    const newOrderNumber = currentList.length + 1;

    const newItem: GradingCriteriaItem = {
      id: newId,
      orderNumber: newOrderNumber,
      title: newForm.title.trim(),
      category: newForm.category,
      description: newForm.description.trim(),
      isUlangkaji: newForm.isUlangkaji,
      maxScore: newForm.maxScore || 5
    };

    setSchemes((prev) => ({
      ...prev,
      [selectedSchemeKey]: {
        ...prev[selectedSchemeKey],
        criteria: [...(prev[selectedSchemeKey].criteria || []), newItem]
      }
    }));

    setNewForm({
      title: '',
      category: 'Gerak Asas',
      description: '',
      isUlangkaji: false,
      maxScore: 5
    });
    setIsAddingNew(false);
    setHasUnsavedChanges(true);
    showToast('success', 'Kriteria Ditambah', `Sukatan "${newItem.title}" berjaya ditambah.`);
  };

  // Save all settings to storage & dispatch update
  const handleSaveAll = () => {
    setIsSaving(true);
    try {
      saveCustomGradingSchemes(schemes);
      saveGradingGeneralRules(generalRules);
      setHasUnsavedChanges(false);
      showToast('success', 'Tetapan Disimpan', 'Semua peraturan dan sukatan silibus ujian berjaya disimpan dan diselaraskan.');
    } catch (err: any) {
      console.error(err);
      showToast('error', 'Ralat Menyimpan', err?.message || 'Gagal menyimpan tetapan.');
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to official default syllabus (Pindaan 2023)
  const handleResetToDefault = () => {
    if (!window.confirm('Adakah anda pasti untuk memulihkan keseluruhan silibus dan peraturan kepada versi rasmi PDF persatuan (Pindaan 2023)? Sebarang penyesuaian tersuai akan dipadamkan.')) {
      return;
    }

    const def = resetCustomGradingSchemes();
    setSchemes(def);
    setHasUnsavedChanges(false);
    showToast('info', 'Silibus Dipulihkan', 'Silibus telah dipulihkan kepada format rasmi Pindaan 2023.');
  };

  // Copy syllabus JSON to clipboard
  const handleCopyJson = () => {
    try {
      const dataStr = JSON.stringify(activeScheme, null, 2);
      navigator.clipboard.writeText(dataStr);
      setCopiedJson(true);
      showToast('success', 'Disalin ke Papan Keratan', 'Data silibus telah disalin dalam format JSON.');
      setTimeout(() => setCopiedJson(false), 2000);
    } catch (err) {
      showToast('error', 'Ralat', 'Gagal menyalin data.');
    }
  };

  // Belt tabs list
  const beltTabs = [
    { key: 'putih1', label: 'Putih Cula 1', desc: 'Pesilat Muda', color: 'bg-white text-slate-900 border-slate-300' },
    { key: 'putih2', label: 'Putih Cula 2', desc: 'Pesilat Kanan', color: 'bg-white text-slate-900 border-slate-300' },
    { key: 'putih3', label: 'Putih Cula 3', desc: 'Pesilat Sulung', color: 'bg-white text-slate-900 border-slate-300' },
    { key: 'merah1', label: 'Merah Cula 1', desc: 'Pembantu Jurulatih Muda', color: 'bg-red-50 text-red-900 border-red-300' },
    { key: 'merah2', label: 'Merah Cula 2', desc: 'Pembantu Jurulatih Kanan', color: 'bg-red-50 text-red-900 border-red-300' },
    { key: 'merah3', label: 'Merah Cula 3', desc: 'Pembantu Jurulatih Sulung', color: 'bg-red-50 text-red-900 border-red-300' },
    { key: 'kuning0', label: 'Kuning', desc: 'Jurulatih & Pendekar', color: 'bg-amber-50 text-amber-900 border-amber-300' },
  ];

  // Calculate metrics
  const totalCriteria = activeScheme?.criteria?.length || 0;
  const totalMaxScore = (activeScheme?.criteria || []).reduce((acc, c) => acc + (c.maxScore || 5), 0);

  if (!isManagement) {
    return (
      <div className="bg-white min-h-[70vh] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 rounded-3xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4 border border-red-100 shadow-sm">
          <Lock className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-black text-slate-900 tracking-tight">Akses Terhad (Pihak Pengurusan Sahaja)</h2>
        <p className="text-sm text-slate-500 max-w-md mt-2">
          Halaman Tetapan Ujian Bengkung hanya dibenarkan untuk pihak pengurusan dan dewan penguji persatuan sahaja.
        </p>
        <button
          type="button"
          onClick={() => navigateBack('/grading')}
          className="mt-6 px-5 py-2.5 rounded-2xl bg-slate-900 text-white font-bold text-xs hover:bg-slate-800 transition shadow-sm cursor-pointer"
        >
          Kembali ke Pentaksiran Calon
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6 sm:py-8 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Standardized Top Header & Navigation Hub */}
        <GradingHeaderHero
          activeTab="settings"
          badgeTag="Pengurusan Peraturan & Silibus Pentaksiran"
          badgeIcon={Sliders}
          badgeColorClass="bg-slate-100 text-slate-800 border-slate-200"
          title="Tetapan Ujian & Pemarkahan Bengkung"
          subtitle="Konfigurasi markah lulus minima, kelayakan dewan penguji, serta penetapan senarai sukatan ujian kenaikan bengkung ASMM."
          metrics={[
            { value: 7, label: 'Tahap Bengkung', colorClass: 'text-slate-900' },
            { value: totalCriteria, label: 'Kriteria Dinilai', colorClass: 'text-blue-600' },
            { value: totalMaxScore, label: 'Maksima Markah', colorClass: 'text-amber-600' },
            { value: `${activeScheme?.passingScorePercentage || 60}%`, label: 'Lulus Minima', colorClass: 'text-red-600' },
          ]}
        >
          {/* Unsaved Changes Banner Alert */}
          {hasUnsavedChanges && (
            <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-2xl flex items-center justify-between gap-3 animate-fade-in max-w-2xl mx-auto text-left">
              <div className="flex items-center gap-2 text-xs font-bold text-amber-900">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>Terdapat perubahan pada tetapan yang belum disimpan ke sistem.</span>
              </div>
              <button
                type="button"
                onClick={handleSaveAll}
                className="px-3.5 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-black transition shadow-xs cursor-pointer shrink-0"
              >
                Simpan Sekarang
              </button>
            </div>
          )}
        </GradingHeaderHero>

        {/* Bengkung Level Tabs Switcher */}
        <div className="bg-white rounded-2xl border border-slate-200 p-2 shadow-xs">
          <div className="text-[11px] font-black text-slate-500 uppercase tracking-wider px-2 py-1 mb-1">
            Pilih Bengkung Untuk Ditetapkan:
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-1.5">
            {beltTabs.map((b) => {
              const isSelected = selectedSchemeKey === b.key;
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => {
                    setSelectedSchemeKey(b.key);
                    setEditingItemId(null);
                    setIsAddingNew(false);
                  }}
                  className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
                    isSelected
                      ? 'border-red-600 bg-red-50/70 ring-2 ring-red-600/20 shadow-xs'
                      : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <div className={`text-xs font-black truncate ${isSelected ? 'text-red-700' : 'text-slate-900'}`}>
                    {b.label}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate mt-0.5">
                    {b.desc}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Panel 1: Peraturan Asas & Syarat Kelulusan Bengkung Terpilih */}
        <div className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-6 shadow-xs space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-base sm:text-lg font-black text-slate-900 flex items-center gap-2">
                <Sliders className="w-4 h-4 text-red-600" />
                <span>Peraturan & Syarat Kelulusan: {activeScheme.displayName}</span>
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Konfigurasi peratus markah lulus dan nama rasmi bagi pendaftaran ini.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopyJson}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold transition cursor-pointer"
                title="Salin JSON Silibus"
              >
                {copiedJson ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-slate-500" />}
                <span>{copiedJson ? 'Disalin' : 'Salin JSON'}</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Display Name Input */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Nama Paparan Rasmi Bengkung:
              </label>
              <input
                type="text"
                value={activeScheme.displayName}
                onChange={(e) => handleDisplayNameChange(e.target.value)}
                className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-200 focus:outline-none focus:border-red-600 bg-white"
                placeholder="Contoh: Bengkung Putih Cula 3"
              />
              <span className="text-[10px] text-slate-400 mt-1 block">
                Kod sasaran pangkalan data: <code className="font-bold text-slate-600">{activeScheme.targetBengkungCode}</code>
              </span>
            </div>

            {/* Passing Score Slider & Input */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-bold text-slate-700">
                  Markah Lulus Minima (%):
                </label>
                <span className="text-xs font-black px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                  {activeScheme.passingScorePercentage}%
                </span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="40"
                  max="95"
                  step="5"
                  value={activeScheme.passingScorePercentage}
                  onChange={(e) => handlePassingScoreChange(Number(e.target.value))}
                  className="w-full accent-red-600 cursor-pointer"
                />
                <input
                  type="number"
                  min="10"
                  max="100"
                  value={activeScheme.passingScorePercentage}
                  onChange={(e) => handlePassingScoreChange(Number(e.target.value))}
                  className="w-16 px-2 py-1 text-center text-xs font-bold rounded-lg border border-slate-200 focus:outline-none focus:border-red-600"
                />
              </div>
              <span className="text-[10px] text-slate-400 mt-1 block">
                Standard rasmi ASMM: Putih 1 (60%), Putih 2 (65%), Putih 3 (70%), Merah (70%-80%).
              </span>
            </div>

            {/* Summary Metrics Pill Bar */}
            <div className="bg-slate-50 rounded-2xl p-3 border border-slate-200 flex items-center justify-around text-center">
              <div>
                <div className="text-[10px] font-bold text-slate-500 uppercase">Kriteria</div>
                <div className="text-base font-black text-slate-900 mt-0.5">{totalCriteria}</div>
                <div className="text-[10px] text-slate-400">pergerakan</div>
              </div>
              <div className="h-8 w-px bg-slate-200" />
              <div>
                <div className="text-[10px] font-bold text-slate-500 uppercase">Markah Penuh</div>
                <div className="text-base font-black text-slate-900 mt-0.5">{totalMaxScore}</div>
                <div className="text-[10px] text-slate-400">skala 5</div>
              </div>
              <div className="h-8 w-px bg-slate-200" />
              <div>
                <div className="text-[10px] font-bold text-slate-500 uppercase">Syarat Skor</div>
                <div className="text-base font-black text-emerald-600 mt-0.5">
                  {Math.ceil((totalMaxScore * activeScheme.passingScorePercentage) / 100)}
                </div>
                <div className="text-[10px] text-slate-400">markah minima</div>
              </div>
            </div>
          </div>
        </div>

        {/* Panel 2: Penetapan & Pengurusan Senarai Sukatan Pelajaran (Editable) */}
        <div className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-6 shadow-xs space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-base sm:text-lg font-black text-slate-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-red-600" />
                <span>Senarai Sukatan Pelajaran ({filteredCriteria.length} daripada {totalCriteria} kriteria)</span>
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Susun semula, tambah, edit tajuk, atau padam pergerakan ujian secara terus.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsAddingNew(true);
                  setEditingItemId(null);
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-black transition shadow-xs cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>Tambah Sukatan Baru</span>
              </button>
            </div>
          </div>

          {/* Search & Category Filter Pills */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Cari pergerakan (cth: Gerakan 1, Tendang, Pukulan)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs font-medium rounded-xl border border-slate-200 focus:outline-none focus:border-red-600 bg-white"
              />
            </div>

            {/* Category Pills */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 custom-scrollbar text-xs">
              <button
                type="button"
                onClick={() => setCategoryFilter('all')}
                className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition shrink-0 cursor-pointer ${
                  categoryFilter === 'all'
                    ? 'bg-slate-900 text-white font-black'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Semua
              </button>
              {CRITERIA_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition shrink-0 cursor-pointer ${
                    categoryFilter === cat
                      ? 'bg-red-600 text-white font-black'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* INLINE FORM: ADD NEW CRITERION */}
          {isAddingNew && (
            <div className="p-4 sm:p-5 rounded-2xl border-2 border-red-500 bg-red-50/20 space-y-3 animate-fade-in">
              <div className="flex items-center justify-between">
                <div className="text-xs font-black text-red-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Plus className="w-4 h-4" />
                  <span>Tambah Kriteria Sukatan Baru ke {activeScheme.displayName}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddingNew(false)}
                  className="p-1 text-slate-400 hover:text-slate-700 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Tajuk Pergerakan / Kriteria:
                  </label>
                  <input
                    type="text"
                    value={newForm.title}
                    onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
                    placeholder="Contoh: Gerakan 1, Pukulan 1.1, Lutut Pacak..."
                    className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-300 bg-white focus:outline-none focus:border-red-600"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Kategori:
                  </label>
                  <select
                    value={newForm.category}
                    onChange={(e) => setNewForm({ ...newForm, category: e.target.value as any })}
                    className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-300 bg-white focus:outline-none focus:border-red-600"
                  >
                    {CRITERIA_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  Keterangan / Panduan Penilaian:
                </label>
                <textarea
                  rows={2}
                  value={newForm.description}
                  onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                  placeholder="Kekuda separuh tiang kemas, koordinasi serangan dan ketepatan sasaran..."
                  className="w-full px-3 py-2 text-xs font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:border-red-600"
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-red-200">
                <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700">
                  <input
                    type="checkbox"
                    checked={newForm.isUlangkaji}
                    onChange={(e) => setNewForm({ ...newForm, isUlangkaji: e.target.checked })}
                    className="w-4 h-4 rounded text-red-600 accent-red-600"
                  />
                  <span>Tandai sebagai sukatan <strong>Ulangkaji</strong> (papar lencana kuning)</span>
                </label>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsAddingNew(false)}
                    className="px-3.5 py-1.5 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-200 transition cursor-pointer"
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    onClick={handleAddNewCriterion}
                    className="px-4 py-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-black transition shadow-xs cursor-pointer"
                  >
                    Simpan Kriteria
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* CRITERIA LIST ITEMS */}
          <div className="divide-y divide-slate-100">
            {filteredCriteria.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-xs">
                Tiada kriteria sukatan dijumpai bagi carian ini.
              </div>
            ) : (
              filteredCriteria.map((item, index) => {
                const isEditing = editingItemId === item.id;
                const canMoveUp = index > 0;
                const canMoveDown = index < filteredCriteria.length - 1;

                if (isEditing) {
                  return (
                    <div key={item.id} className="py-4 space-y-3 bg-slate-50 p-4 rounded-2xl my-2 border border-slate-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-black text-slate-800">
                          Edit Kriteria #{item.orderNumber}
                        </span>
                        <button
                          type="button"
                          onClick={() => setEditingItemId(null)}
                          className="p-1 text-slate-400 hover:text-slate-700 cursor-pointer"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2">
                          <label className="block text-[11px] font-bold text-slate-700 mb-1">Tajuk:</label>
                          <input
                            type="text"
                            value={editForm.title}
                            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                            className="w-full px-3 py-1.5 text-xs font-bold rounded-xl border border-slate-300 bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-slate-700 mb-1">Kategori:</label>
                          <select
                            value={editForm.category}
                            onChange={(e) => setEditForm({ ...editForm, category: e.target.value as any })}
                            className="w-full px-3 py-1.5 text-xs font-bold rounded-xl border border-slate-300 bg-white"
                          >
                            {CRITERIA_CATEGORIES.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">Keterangan:</label>
                        <textarea
                          rows={2}
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          className="w-full px-3 py-1.5 text-xs font-medium rounded-xl border border-slate-300 bg-white"
                        />
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700">
                          <input
                            type="checkbox"
                            checked={editForm.isUlangkaji}
                            onChange={(e) => setEditForm({ ...editForm, isUlangkaji: e.target.checked })}
                            className="w-4 h-4 rounded text-red-600 accent-red-600"
                          />
                          <span>Tandai sebagai Ulangkaji</span>
                        </label>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setEditingItemId(null)}
                            className="px-3 py-1 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-lg cursor-pointer"
                          >
                            Batal
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSaveEdit(item.id)}
                            className="px-3.5 py-1 text-xs font-black text-white bg-red-600 hover:bg-red-700 rounded-lg cursor-pointer shadow-2xs"
                          >
                            Simpan Kemaskini
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={item.id} className="py-3 hover:bg-slate-50/60 transition px-2 rounded-xl flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Badges row: placed above title, aligned with title text start */}
                      <div className="flex items-center gap-1.5 flex-wrap ml-[38px] mb-1">
                        <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                          {item.category}
                        </span>
                        {item.isUlangkaji && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                            Ulangkaji
                          </span>
                        )}
                        <span className="text-[9px] font-bold text-slate-400 ml-auto">
                          Maks: {item.maxScore || 5} markah
                        </span>
                      </div>

                      {/* Numbering + Title Row (Matching horizontal top coordinate) */}
                      <div className="flex items-start gap-2.5">
                        {/* Order Badge & Reorder Buttons */}
                        <div className="flex flex-col items-center gap-1 shrink-0">
                          <div className="w-7 h-7 rounded-xl bg-slate-900 text-white font-black text-xs flex items-center justify-center shadow-2xs">
                            {item.orderNumber}
                          </div>
                          <div className="flex items-center gap-0.5">
                            <button
                              type="button"
                              disabled={!canMoveUp}
                              onClick={() => handleMoveUp(item.id)}
                              className="p-1 rounded text-slate-400 hover:text-slate-800 hover:bg-slate-200 disabled:opacity-20 cursor-pointer disabled:cursor-not-allowed"
                              title="Alih ke atas"
                            >
                              <ArrowUp className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              disabled={!canMoveDown}
                              onClick={() => handleMoveDown(item.id)}
                              className="p-1 rounded text-slate-400 hover:text-slate-800 hover:bg-slate-200 disabled:opacity-20 cursor-pointer disabled:cursor-not-allowed"
                              title="Alih ke bawah"
                            >
                              <ArrowDown className="w-3 h-3" />
                            </button>
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="text-xs sm:text-sm font-extrabold text-slate-900 leading-snug">
                            {item.title}
                          </h3>

                          {item.description && (
                            <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                              {item.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row Action Buttons: Edit & Delete */}
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <button
                        type="button"
                        onClick={() => handleStartEdit(item)}
                        className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-200 transition cursor-pointer"
                        title="Edit Kriteria"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteCriterion(item.id, item.title)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition cursor-pointer"
                        title="Padam Kriteria"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Panel 3: Tetapan Global Kelayakan Penguji & Format Sijil */}
        <div className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-6 shadow-xs space-y-4">
          <div className="border-b border-slate-100 pb-3">
            <h2 className="text-base sm:text-lg font-black text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span>Kelayakan Dewan Penguji & Format Sijil ASMM</span>
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Kawal had kelayakan penguji dan penyeragaman no. rujukan sijil rasmi persatuan.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Kelayakan Minima Penguji:
              </label>
              <select
                value={generalRules.minExaminerBengkung}
                onChange={(e) => {
                  setGeneralRules({ ...generalRules, minExaminerBengkung: e.target.value });
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-red-600"
              >
                <option value="Bengkung Kuning">Bengkung Kuning dan ke atas (Jurulatih, Dewan Pendekar & Guru Utama)</option>
                <option value="Bengkung Merah 3">Bengkung Merah Cula 3 dan ke atas (Pembantu Jurulatih Sulung)</option>
                <option value="Bengkung Merah 1">Bengkung Merah Cula 1 dan ke atas (Semua Pembantu Jurulatih)</option>
              </select>
              <span className="text-[10px] text-slate-400 mt-1 block">
                Standard ROS: Hanya pemegang Bengkung Kuning bertauliah layak menilai dan menandatangani keputusan.
              </span>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Format Awalan No. Sijil (Certificate Template):
              </label>
              <input
                type="text"
                value={generalRules.certificatePrefixTemplate}
                onChange={(e) => {
                  setGeneralRules({ ...generalRules, certificatePrefixTemplate: e.target.value });
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-red-600 font-mono"
                placeholder="ASMM/GRD/{YEAR}/{ID}"
              />
              <span className="text-[10px] text-slate-400 mt-1 block">
                Tag disokong: <code>{'{YEAR}'}</code> untuk tahun semasa, <code>{'{ID}'}</code> untuk nombor calon.
              </span>
            </div>
          </div>
        </div>

        {/* Bottom Floating Action Bar */}
        <div className="sticky bottom-4 z-20 bg-white/95 backdrop-blur-md p-4 rounded-2xl border border-slate-200 shadow-lg flex flex-col sm:flex-row items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleResetToDefault}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-bold transition cursor-pointer"
          >
            <RotateCcw className="w-3.5 h-3.5 text-slate-500" />
            <span>Pulihkan Silibus Asal (Pindaan 2023)</span>
          </button>

          <div className="w-full sm:w-auto flex items-center justify-end gap-2">
            <button
              type="button"
              disabled={isSaving}
              onClick={handleSaveAll}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 active:scale-95 text-white text-xs font-black transition shadow-md cursor-pointer disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full" />
                  <span>Menyimpan Tetapan...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 text-amber-300" />
                  <span>Simpan Semua Peraturan & Silibus</span>
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
